import { TriangleAlert } from "lucide-react";

import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";

import { HIGHLIGHT_BLURB_MAX, HIGHLIGHT_TITLE_MAX } from "../../events/landing";
import type { HighlightForm, HighlightProblems } from "../agenda-highlight";
import { LimitedField } from "./LimitedField";

/**
 * Promotes a session to a chapter on the landing page: a switch, and when it
 * is on, a short title and a card text. Both texts are optional and fall back
 * to the session's own title and description, which the helper text says.
 *
 * The switch row is one label spanning the line, so the whole row is the touch
 * target (44px tall) and not only the 44 by 24 pixel switch.
 *
 * The chapter warning has a live region that is always in the page, because a
 * message inserted with its text is often not announced.
 */
export function AgendaHighlightFields({
  value,
  onChange,
  problems,
  warning,
  disabled,
  stored,
}: {
  value: HighlightForm;
  onChange: (next: HighlightForm) => void;
  problems: HighlightProblems;
  warning: string | null;
  disabled?: boolean;
  /** The texts already saved on the session; blank for a new one. */
  stored?: { title: string; blurb: string };
}) {
  const offNote = value.on ? null : keptNote(value, stored);
  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <Label
        htmlFor="agenda-highlight"
        className="flex min-h-11 cursor-pointer items-center justify-between gap-4 text-sm font-medium"
      >
        <span>
          Highlight on the landing page
          <span className="block text-xs font-normal text-muted-foreground">
            Shows this session as a numbered chapter below the hero.
          </span>
        </span>
        <Switch
          id="agenda-highlight"
          data-testid="agenda-highlight"
          checked={value.on}
          disabled={disabled}
          onCheckedChange={(on) => onChange({ ...value, on })}
        />
      </Label>

      {/* Hidden rather than disabled while off: two greyed-out fields cost a
          320px screen more than the sentence does. The values stay in the
          form's state so switching back on shows them, but nothing is sent
          for them while off, so the note says which of them survive a save. */}
      {offNote && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="agenda-highlight-kept"
        >
          {offNote}
        </p>
      )}

      {value.on && (
        <>
          <LimitedField
            id="agenda-highlight-title"
            label="Chapter title"
            value={value.title}
            max={HIGHLIGHT_TITLE_MAX}
            count={value.title.trim().length}
            disabled={disabled}
            hint="Leave empty to use the session title."
            error={problems.title}
            onChange={(title) => onChange({ ...value, title })}
          />
          <LimitedField
            id="agenda-highlight-blurb"
            label="Chapter text"
            value={value.blurb}
            max={HIGHLIGHT_BLURB_MAX}
            count={value.blurb.trim().length}
            multiline
            rows={4}
            disabled={disabled}
            hint="Leave empty to use the start of the session description."
            error={problems.blurb}
            onChange={(blurb) => onChange({ ...value, blurb })}
          />
        </>
      )}

      <p
        role="status"
        data-testid="agenda-highlight-warning"
        className={
          warning
            ? "flex items-start gap-2 rounded-md border border-status-warning bg-status-warning/15 px-3 py-2 text-sm text-foreground"
            : "sr-only"
        }
      >
        {warning && (
          <TriangleAlert
            className="mt-0.5 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
        )}
        {warning}
      </p>
    </div>
  );
}

/**
 * What a save does with the chapter texts while the switch is off, said
 * exactly: the saved ones stay in the event, and anything typed in this dialog
 * since is not sent.
 */
function keptNote(
  value: HighlightForm,
  stored: { title: string; blurb: string } = { title: "", blurb: "" },
): string | null {
  const title = value.title.trim();
  const blurb = value.blurb.trim();
  if (stored.title || stored.blurb) {
    const changed = title !== stored.title || blurb !== stored.blurb;
    return `The saved chapter title and text stay as they are for when you switch it on again.${changed ? " What you have typed here since is not saved." : ""}`;
  }
  return title || blurb
    ? "The chapter title and text you typed here are not saved while the switch is off."
    : null;
}
